
import { IAssistantCart, IAssistantMessage, IQuestionOption, WindowAnnotation } from "../types";
import React from "react";
import {
  EXTERNAL_COLOR_OPTIONS,
  FOIL_SURCHARGE_RESOLVER,
  MESSAGE_TYPE_ENUM,
  PROFILE_COLOR_OPTION,
} from "../constants";
import { useAssistant } from "../context";

/**
 * Returns true if any already-saved cart window carries the Specials Foil flat
 * surcharge, so the window currently being configured can skip adding it again.
 */
export const useFoilSurchargeExistsInCart = (): boolean => {
  const { state } = useAssistant();
  return React.useMemo(
    () =>
      (state.cart?.windowConfiguration ?? []).some((w) =>
        w.messages.some(
          (m) =>
            m.type === MESSAGE_TYPE_ENUM.priceSurge &&
            (m.surgeItems ?? []).some(
              (s: any) => s.resolverValue === FOIL_SURCHARGE_RESOLVER,
            ),
        ),
      ),
    [state.cart?.windowConfiguration],
  );
};

export const useGetBlendingResources = ({
  cart,
  originalImage,
  windows,
}: {
  cart?: IAssistantCart;
  originalImage?: string;
  windows: WindowAnnotation[];
}) => {
  const windowData = React.useMemo((): {
    window: WindowAnnotation | undefined;
    originalImage?: string;
  } => {
    // If no windows exist, return undefined to disable blending
    if (!windows.length) {
      return {
        window: undefined,
        originalImage: undefined,
      };
    }

    // If no original property image, return undefined
    if (!originalImage) {
      return {
        window: undefined,
        originalImage: undefined,
      };
    }

    // Get all configurations from cart
    const configurations = cart?.windowConfiguration || [];

    // Find configurations that match the current original property image
    // This allows us to handle the case where the property image changes
    const matchingConfigurations = configurations.filter(
      (config) => config.propertyImageUrl === originalImage,
    );

    // Get all window IDs from matching configurations.
    // Use window.id (the actual original window used) — windowAnnotationId can be stale
    // on old/duplicated items, so window.id is the authoritative identifier.
    const cartWindowIds = new Set(
      matchingConfigurations
        .map((item) => item.window?.id)
        .filter(Boolean),
    );

    // Get the set of configured window IDs for the current original image
    const configuredWindowIds = cartWindowIds;

    // Find the next window that hasn't been configured yet
    // This handles both continuing from where we left off and processing new windows
    const nextWindowIndex = windows.findIndex(
      (w) => !configuredWindowIds.has(w.id),
    );

    // If all current windows are configured for this original image, return undefined
    // This takes priority over any window changes - if everything is configured, we're done
    if (nextWindowIndex === -1) {
      return {
        window: undefined,
        originalImage: undefined,
      };
    }

    // Check if the window boxes have changed
    // Get current window IDs
    const currentWindowIds = new Set(windows.map((w) => w.id).filter(Boolean));

    // Check if we've already started configuring the current window set
    // If any current window is already configured, we should continue sequentially
    const hasStartedCurrentSet = windows.some(
      (w) => w.id && configuredWindowIds.has(w.id),
    );

    // Check if any configured windows are no longer in the current windows array
    // This indicates windows have been removed or detection has been re-run
    const hasWindowsRemoved = Array.from(cartWindowIds).some(
      (id) => id && !currentWindowIds.has(id),
    );

    // Only restart if windows were removed AND we haven't started the current set yet
    // This prevents restarting in the middle of configuring a new window set
    if (
      !hasStartedCurrentSet &&
      hasWindowsRemoved &&
      matchingConfigurations.length > 0
    ) {
      return {
        window: windows[0],
        originalImage: originalImage,
      };
    }

    const nextWindow = windows[nextWindowIndex];

    // Determine the image to use for blending
    let imageForBlending = originalImage;

    // If this is not the first window, we need to use the blended image from the previous window
    if (nextWindowIndex > 0) {
      const previousWindow = windows[nextWindowIndex - 1];
      const previousConfiguration = matchingConfigurations.find(
        (item) => item.window?.id === previousWindow?.id,
      );

      if (previousConfiguration) {
        // Use the blended result from the previous configuration
        // Priority: blendedImageUrl > original
        imageForBlending =
          previousConfiguration.blendedImageUrl || originalImage;
      }
      // If previous configuration not found, fall back to original image
      // This handles the case where window boxes are added in between
    }

    return {
      window: nextWindow,
      originalImage: imageForBlending,
    };
  }, [windows, cart?.windowConfiguration, originalImage]);
  return windowData;
};

export const useGetEditBlendingResources = ({
  cart,
  editingItemId,
}: {
  cart?: IAssistantCart;
  editingItemId?: string;
}) => {
  const windowData = React.useMemo((): {
    window: WindowAnnotation | undefined;
    index: number;
    offlineImage?: string;
    points: any[];
  } => {
    const editItem = cart?.windowConfiguration.find(
      (item) => item.id == editingItemId,
    );
    if (!editItem) {
      return {
        window: undefined,
        index: -1,
        offlineImage: undefined,
        points: [],
      };
    }
    // If no window annotation id, still allow blending with the property image
    // (coordinates will be empty — blend service masks the full image)
    if (!editItem?.window?.id) {
      return {
        window: editItem.window,
        index: 0,
        offlineImage: editItem?.propertyImageUrl,
        points: [],
      };
    }
    return {
      window: editItem.window,
      index: 0,
      offlineImage: (() => {
        // For single-item carts always use the raw property image.
        // For multi-item carts prefer the stored blended image — but guard against
        // blob URLs (session-only, invalid after reload) and fall back to propertyImageUrl.
        if (cart?.windowConfiguration?.length === 1) return editItem?.propertyImageUrl;
        const stored = editItem.blendedImageUrl;
        return stored && !stored.startsWith('blob:') ? stored : editItem?.propertyImageUrl;
      })(),
      // Use all blended window boxes when available (multi-window duplicate),
      // otherwise fall back to the single primary window.
      points:
        editItem.windows && editItem.windows.length > 0
          ? editItem.windows.map(w => ({ x: w.x, y: w.y, width: w.width, height: w.height }))
          : [{ x: editItem.window.x, y: editItem.window.y, width: editItem.window.width, height: editItem.window.height }],
    };
  }, [cart?.windowConfiguration, editingItemId]);
  return windowData;
};

export const useGetTemplateColors = ({
  messages,
}: {
  messages: IAssistantMessage[];
}) => {
  // Pure derivation from messages (not useState+useEffect) — when the user
  // re-answers an earlier question and downstream messages get truncated,
  // this must recompute to "" immediately rather than keep whatever color
  // was last set, which would otherwise leak a stale color onto a freshly
  // (re)selected product before the color question is even answered again.
  const color = React.useMemo(() => {
    const isValidHexColor = (color: string): boolean => {
      // Check if color matches hex format: #RGB, #RRGGBB, or #RRGGBBAA
      const hexColorRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
      return hexColorRegex.test(color);
    };

    const userMessages = messages.find(
      (item) =>
        item.type === MESSAGE_TYPE_ENUM.user &&
        item.question?.optionType === PROFILE_COLOR_OPTION,
    );
    const allProfileColorMessages = messages.filter((msg) =>
      msg.question?.displayCondition?.dependsOn?.some(
        (item) =>
          item.categoryId === userMessages?.question?.category &&
          item.optionIds?.includes(
            userMessages?.response?.selectedOptionsIds?.[0] ?? "",
          ),
      ),
    );
    let templateColor: string | undefined;
    // Prefer an external-colour dependent's value (e.g. external foil colour,
    // hardware colour) ONLY when one of those SPECIFIC dependents is actually
    // present and answered — not merely because *some* dependent question
    // exists for Profile Colour at all. Any other dependent (decorative bar
    // colour, cill-match alerts, etc.) previously suppressed the fallback
    // entirely, leaving templateColor undefined → color reset to "" even
    // though Profile Colour itself had a perfectly valid hex answer. That
    // made the frame colour silently vanish whenever editing an item whose
    // messages already contained ANY Profile-Colour-dependent question.
    const colorMessage = allProfileColorMessages?.find((item) =>
      EXTERNAL_COLOR_OPTIONS.includes(item.question?.optionType),
    );
    if (colorMessage) {
      colorMessage?.response?.answer?.forEach((item) => {
        templateColor = item.value;
      });
    } else {
      userMessages?.response?.answer?.forEach((item) => {
        templateColor = item.value;
      });
    }
    return templateColor && isValidHexColor(templateColor) ? templateColor : "";
  }, [messages]);

  return { color };
};

export const DBR_OPTION = 2;

export const useGetDBRAnswer = ({
  messages,
}: {
  messages: IAssistantMessage[];
}) => {
  // Pure derivation from messages (not useState+useEffect) — when the user
  // re-answers an earlier question and downstream messages get truncated,
  // this must recompute to null immediately rather than keep whatever DBR
  // answer was last set, which would otherwise leak a stale DBR result onto
  // a freshly (re)selected product before the DBR question is even answered
  // again.
  const dbrAnswer = React.useMemo<IQuestionOption | null>(() => {
    const dbrMessage = messages.find(
      (msg) =>
        msg.type === MESSAGE_TYPE_ENUM.user &&
        msg.question?.optionType === DBR_OPTION,
    );
    return dbrMessage?.response?.answer?.[0] ?? null;
  }, [messages]);

  const dbrAnswerText = (dbrAnswer?.value ?? dbrAnswer?.optionText ?? "").toLocaleLowerCase();

  return {
    dbrAnswer,
    isDbrEnabled:
      dbrAnswer !== null &&
      !dbrAnswerText.includes("without") &&
      (dbrAnswerText === "yes" || dbrAnswerText.includes("with")),
  };
};

